import { Validator } from 'vee-validate'
import { Settings } from 'luxon'
import Vue from 'vue'
import VueI18n from 'vue-i18n'
import messages from '@/locales'
import moment from 'moment'
import veeEn from 'vee-validate/dist/locale/en'
import veePl from 'vee-validate/dist/locale/pl'
import veeSk from 'vee-validate/dist/locale/sk'
import 'moment/locale/pl'
import 'moment/locale/sk'

export const SUPPORTED_LOCALES = ['pl', 'en', 'sk']
export const DEFAULT_LOCALE = 'pl'
export const FALLBACK_LOCALE = 'en'

const STORAGE_KEY = 'locale'

Vue.use(VueI18n)

/**
 * Odczyt zapisanego jezyka. Trzymamy go recznie w localStorage, spojnie z
 * reszta repo (vuex-persistedstate jest w zaleznosciach, ale nieuzywany).
 * Zwraca null, gdy uzytkownik nigdy nie wybral jezyka albo w storage siedzi
 * kod, ktorego juz nie obslugujemy.
 */
export function getStoredLocale() {
  let stored = null

  try {
    stored = localStorage.getItem(STORAGE_KEY)
  } catch (e) {
    stored = null
  }

  return SUPPORTED_LOCALES.indexOf(stored) !== -1 ? stored : null
}

/**
 * Jezyk z przegladarki. `navigator.languages` to lista preferencji uzytkownika
 * w kolejnosci waznosci, `navigator.language` to tylko pierwsza pozycja — stad
 * najpierw lista, potem pojedyncza wartosc jako fallback dla starszych silnikow.
 *
 * Tagi przychodza jako 'sk-SK', 'pl', 'en-GB' — porownujemy sam kod jezyka,
 * bo regionu nie rozrozniamy. Nieobslugiwany jezyk (np. 'de') -> null.
 */
export function getBrowserLocale() {
  if (typeof navigator === 'undefined') return null

  const candidates = []

  if (navigator.languages && navigator.languages.length) {
    Array.prototype.push.apply(candidates, navigator.languages)
  }

  if (navigator.language) candidates.push(navigator.language)

  for (let i = 0; i < candidates.length; i++) {
    const language = String(candidates[i] || '')
      .toLowerCase()
      .split('-')[0]

    if (SUPPORTED_LOCALES.indexOf(language) !== -1) return language
  }

  return null
}

/**
 * Jezyk na start aplikacji. Kolejnosc jest wazna:
 *   1. wybor uzytkownika z localStorage — raz kliknieta flaga wygrywa zawsze
 *   2. jezyk przegladarki — tylko przy pierwszym wejsciu, zeby slowacki klient
 *      nie ladowal w polskim UI
 *   3. polski — gdy przegladarka mowi czyms, czego nie mamy
 *
 * Wykryty jezyk NIE laduje od razu w localStorage: dopiero swiadome klikniecie
 * w przelaczniku (`setLocale`) utrwala wybor. Dzieki temu zmiana ustawien
 * przegladarki dalej dziala, a nie zostaje zabetonowana pierwszym wejsciem.
 */
export function getInitialLocale() {
  return getStoredLocale() || getBrowserLocale() || DEFAULT_LOCALE
}

/**
 * vue-i18n@8 zna z pudelka tylko `one | other`. Polski i slowacki maja trzy
 * formy, wiec dla `pl` i `sk` wybieramy indeks recznie:
 *   0 -> 1 element
 *   1 -> 2,3,4 elementy
 *   2 -> 5+ elementow
 * Klucze mnogie zapisujemy jako "element | elementy | elementow".
 *
 * Roznica miedzy jezykami jest w liczbach powyzej 20: polski patrzy na koncowke
 * (22 -> forma druga, "22 pliki"), slowacki nie (22 -> forma trzecia,
 * "22 suborov"). Stad dwie galezie zamiast jednej.
 */
VueI18n.prototype.getChoiceIndex = function (choice, choicesLength) {
  const locale = this.locale

  if (locale !== 'pl' && locale !== 'sk') {
    // zachowanie domyslne vue-i18n dla pozostalych jezykow
    if (choicesLength === 2) return choice ? (choice > 1 ? 1 : 0) : 1
    return Math.min(choice, choicesLength - 1)
  }

  if (choicesLength < 3) return choice === 1 ? 0 : 1

  if (choice === 1) return 0

  if (locale === 'sk') {
    return choice >= 2 && choice <= 4 ? 1 : 2
  }

  const mod10 = choice % 10
  const mod100 = choice % 100

  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 1
  return 2
}

const i18n = new VueI18n({
  locale: getInitialLocale(),
  fallbackLocale: FALLBACK_LOCALE,
  // brak tlumaczenia = cichy fallback na angielski, bez zasmiecania konsoli
  silentFallbackWarn: true,
  messages
})

/**
 * Slowniki vee-validate 2. Wlasna regula `phone` nie ma juz `getMessage`
 * zaszytego po angielsku w setup/validation.js — komunikat jedzie stad.
 */
/**
 * Nazwy pol wstrzykiwane do komunikatow walidacji. Bez tego vee-validate bierze
 * atrybut `name` z inputa i wychodzi „Pole firstName jest wymagane".
 * Klucz = wartosc `name`/`data-vv-as` z formularza.
 */
const veeAttributes = {
  en: {
    name: 'name',
    firstName: 'first name',
    lastName: 'last name',
    email: 'e-mail',
    password: 'password',
    newPassword: 'new password',
    oldPassword: 'old password',
    passwordConfirmed: 'password confirmation',
    password_confirmed: 'password confirmation',
    username: 'login',
    phone: 'phone number',
    street: 'street',
    zipCode: 'postcode',
    website: 'website',
    url: 'URL',
    number: 'number',
    newCity: 'city',
    hotelPms: 'PMS system'
  },
  pl: {
    name: 'nazwa',
    firstName: 'imię',
    lastName: 'nazwisko',
    email: 'e-mail',
    password: 'hasło',
    newPassword: 'nowe hasło',
    oldPassword: 'stare hasło',
    passwordConfirmed: 'potwierdzenie hasła',
    password_confirmed: 'potwierdzenie hasła',
    username: 'login',
    phone: 'numer telefonu',
    street: 'ulica',
    zipCode: 'kod pocztowy',
    website: 'strona www',
    url: 'adres URL',
    number: 'numer',
    newCity: 'miasto',
    hotelPms: 'system PMS'
  },
  sk: {
    name: 'názov',
    firstName: 'meno',
    lastName: 'priezvisko',
    email: 'e-mail',
    password: 'heslo',
    newPassword: 'nové heslo',
    oldPassword: 'staré heslo',
    passwordConfirmed: 'potvrdenie hesla',
    password_confirmed: 'potvrdenie hesla',
    username: 'prihlasovacie meno',
    phone: 'telefónne číslo',
    street: 'ulica',
    zipCode: 'PSČ',
    website: 'webová stránka',
    url: 'adresa URL',
    number: 'číslo',
    newCity: 'mesto',
    hotelPms: 'systém PMS'
  }
}

const veeDictionary = {
  en: {
    name: veeEn.name,
    messages: Object.assign({}, veeEn.messages, {
      phone: field => `${field} is not a valid phone number`
    }),
    attributes: Object.assign({}, veeEn.attributes, veeAttributes.en)
  },
  pl: {
    name: veePl.name,
    messages: Object.assign({}, veePl.messages, {
      phone: field => `Pole ${field} nie zawiera poprawnego numeru telefonu`
    }),
    attributes: Object.assign({}, veePl.attributes, veeAttributes.pl)
  },
  sk: {
    name: veeSk.name,
    messages: Object.assign({}, veeSk.messages, {
      // reszta slownika sk mowi "Polozka {field} ..." — trzymamy sie tego wzorca
      phone: field => `Položka ${field} neobsahuje platné telefónne číslo`
    }),
    attributes: Object.assign({}, veeSk.attributes, veeAttributes.sk)
  }
}

Validator.localize(veeDictionary)

/**
 * Jedyne miejsce, w ktorym zmienia sie jezyk. Poza samym UI przestawia
 * formatowanie dat (moment + luxon), slownik walidacji i atrybut lang
 * na <html>, zeby nie wyszla hybryda „Anuluj / Monday".
 */
export function setLocale(locale) {
  if (SUPPORTED_LOCALES.indexOf(locale) === -1) return i18n.locale

  i18n.locale = locale

  try {
    localStorage.setItem(STORAGE_KEY, locale)
  } catch (e) {
    // prywatny tryb przegladarki — jezyk zadziala tylko na czas sesji
  }

  moment.locale(locale)
  Settings.defaultLocale = locale
  Validator.localize(locale)

  if (typeof document !== 'undefined' && document.documentElement) {
    document.documentElement.setAttribute('lang', locale)
  }

  return locale
}

// pierwsze ustawienie — synchronizuje daty i walidacje z odczytanym jezykiem
setLocale(i18n.locale)

export default i18n
